import {SearchIcon, XIcon} from '@heroicons/react/solid';
import {isEmpty} from 'lodash-es';
import type {ReactElement} from 'react';
import {useEffect} from 'react';

import Button from '@/components/common/Button';
import {Input} from '@/components/common/Input';

import useSearchBar from './useSearchBar';

type SearchBarProps = {
  handleSearch: (searchTerm: string) => void;
  handleAutoComplete?: (searchTerm: string) => void;
  placeholder?: string;
  dataCy?: string;
  clearFilters?: () => void;
  inputClassName?: string;
  /** The text of a search kept outside the box (the URL). The box follows it. */
  initialSearchTerm?: string;
};
const SearchBar = ({
  handleSearch,
  handleAutoComplete,
  placeholder,
  clearFilters,
  dataCy,
  inputClassName,
  initialSearchTerm,
}: SearchBarProps): ReactElement => {
  const {
    searchTerm,
    handleSearchTermChange,
    handleKeyDown,
    cancelPendingAutoComplete,
  } = useSearchBar({
    handleSearch,
    handleAutoComplete,
    initialSearchTerm,
  });

  const clearSearch = () => {
    //cancel first, else the last keystroke's autocomplete is still queued and
    //fires ~500ms later, wiping the full list we are about to restore
    cancelPendingAutoComplete();
    handleSearchTermChange({target: {value: ''}});
    handleSearch('');
    clearFilters?.();
  };

  useEffect(() => {
    if (searchTerm?.trim() === '') {
      //deleting all the text by hand should behave like the Clear button
      cancelPendingAutoComplete();
      clearFilters?.();
    }
    //cancelPendingAutoComplete is a stable useCallback, and clearFilters changes
    //identity every render (adding it here would loop), so both are omitted
  }, [searchTerm]);

  return (
    <div
      className={`flex items-center h-14 ${inputClassName} border border-gray-300 dark:border-dark-borderHeavy rounded-md focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 bg-white dark:bg-transparent`}
    >
      <div className="flex w-full items-center">
        <Input
          onChange={handleSearchTermChange}
          onKeyDown={handleKeyDown}
          aria-label={placeholder}
          value={searchTerm || ''}
          name="search"
          type="search"
          placeholder={placeholder || 'Search Users'}
          data-cy={dataCy && `${dataCy}-search-input`}
          leftIcon={<SearchIcon className="h-5 w-5 text-gray-400" />}
          autoComplete="off"
          className="border-none focus:shadow-none h-12 w-full isFull dark:border-dark-borderHeavy dark:bg-transparent dark:focused:bg-transparent"
        />

        <div className="flex gap-2 items-center ml-2">
          <Button
            onClick={clearSearch}
            data-cy={dataCy && `${dataCy}-search-input-clear-button`}
            title="Clear search"
            variant="ghost"
            className="h-8 w-8 hidden md:flex rounded-md items-center justify-center hover:border-cyan-600 hover:text-cyan-600 group"
          >
            <XIcon className="h-5 w-5 text-gray-400 group-hover:text-cyan-600" />
          </Button>
          <Button
            onClick={() => handleSearch(searchTerm)}
            variant="solid"
            data-cy={dataCy && `${dataCy}-search-input-search-button`}
            className="flex rounded-tl-none rounded-bl-none items-center text-sm font-semibold text-white cursor-pointer px-3 bg-cyan-600 h-14 rounded-tr-md rounded-br-md"
            disabled={isEmpty(searchTerm)}
          >
            Search
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SearchBar;
